import type {
  ChangeEvent,
  FC,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  SyntheticEvent,
} from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import type {
  AutocompleteProps,
  SxProps,
  TextFieldProps,
  Theme,
} from '@mui/material';
import {
  Autocomplete as MuiAutocomplete,
  Checkbox,
  CircularProgress,
  createFilterOptions,
  FormControl,
  ListItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import type {
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete/Autocomplete';
import type {
  AutocompleteChangeReason,
  AutocompleteValue,
} from '@mui/base/useAutocomplete/useAutocomplete';
import type { Option, OptionValue } from 'types/shared';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { isEmpty } from 'lodash-es';
import CustomScrollbarListbox from './CustomScrollbarListbox';

export interface ControlAutocompleteProps
  extends Pick<
    AutocompleteProps<OptionValue, true, boolean, false>,
    'disableClearable'
  > {
  value?: OptionValue[];
  onChange: (value: OptionValue[]) => void;
  onClose?: () => void;
  InputProps?: TextFieldProps;
  id: string;
  isLoading?: boolean;
  options: Option[];
  allSelectable?: boolean;
  label?: string;
  name: string;
  required?: boolean;
  tooltipTitle?: string;
  additionalLabel?: ReactElement;
  renderTags?: (
    tagValue: Option[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => ReactNode | undefined;
  sx?: SxProps<Theme>;
  innerlabel?: boolean;
  open?: boolean;
  error?: string;
  loading?: boolean;
  loadMoreRef?: (node?: Element | null) => void;
  dataTestId?: string;
}

// eslint-disable-next-line react/display-name
const ControlAutocomplete: FC<ControlAutocompleteProps> = forwardRef(
  (
    {
      id,
      options,
      isLoading,
      onChange,
      onClose,
      value,
      InputProps,
      allSelectable = true,
      tooltipTitle,
      sx,
      open,
      loadMoreRef,
      loading,
      dataTestId,
      ...props
    },
    ref // Controller is passing down a ref to the ControlAutocomplete component. Since functional components don't accept refs by default.
  ) => {
    const [inputValue, setInputValue] = useState('');
    const { setValue, trigger } = useFormContext();
    const isIndeterminate = useWatch({ name: 'indeterminate' }) as boolean;

    const derivedValue = useMemo(
      () => options.filter((o) => value?.includes(o.value)),
      [value, options]
    );

    const allSelected = options.length === derivedValue.length;

    const handleSelectAll = () => {
      if (allSelected) {
        onChange([]);
      } else {
        onChange(
          options.filter((o) => o.value !== 'select-all').map((o) => o.value)
        );
      }
    };

    const filter = createFilterOptions<Option>();

    const optionRenderer = (
      onChange?: (option: Option) => void,
      isIndeterminate?: boolean,
      loadMoreRef?: (node?: Element | null) => void
    ) => {
      // eslint-disable-next-line react/display-name
      return (
        props: HTMLAttributes<HTMLLIElement> & { key: string | number },
        option: Option,
        state: AutocompleteRenderOptionState,
        ownerState: AutocompleteOwnerState<Option, true, false, false>
      ) => {
        const { key, ...otherProps } = props;
        const selectAllProps =
          option.value === 'select-all' ? { checked: allSelected } : {};
        const isLastElement =
          ownerState.options.indexOf(option) === ownerState.options.length - 1;
        return (
          <ListItem
            key={key ? key : `${option.label}${option.value}`}
            {...otherProps}
            ref={isLastElement ? loadMoreRef : undefined}
          >
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              checked={state.selected}
              indeterminate={option.indeterminate}
              onChange={
                !isEmpty(isIndeterminate) ? () => onChange?.(option) : undefined
              }
              {...selectAllProps}
            />
            {option.label}
          </ListItem>
        );
      };
    };

    const handleChange = async (
      _: SyntheticEvent,
      newValue: AutocompleteValue<Option, true, false, false>,
      reason: AutocompleteChangeReason
    ) => {
      if (reason === 'selectOption' || reason === 'removeOption') {
        if (newValue.find((option) => option.value === 'select-all')) {
          handleSelectAll();
        } else {
          onChange(newValue.map((o) => o.value));
        }
      } else if (reason === 'clear') {
        onChange([]);
        setValue('indeterminate', {});
      }
      await trigger(props.name);
    };

    const onChangeCheckbox = useCallback(
      (option: Option) => {
        setValue(`indeterminate.${option.value}`, false);
      },
      [setValue]
    );

    const handleInputValue = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
      },
      [setInputValue]
    );

    const handleClose = useCallback(() => {
      setInputValue('');
      onClose?.();
    }, [setInputValue, onClose]);

    const disabled = !!isLoading || !!InputProps?.disabled;

    return (
      <Tooltip
        title={disabled && tooltipTitle ? tooltipTitle : ''}
        placement="top"
        arrow
      >
        <MuiAutocomplete
          {...props}
          ref={ref}
          size="small"
          multiple
          disabled={disabled}
          loading={loading ?? isLoading}
          id={id}
          options={options}
          disableCloseOnSelect
          disableClearable={false}
          value={derivedValue}
          getOptionLabel={(option) => option.label ?? ''}
          renderOption={optionRenderer(
            onChangeCheckbox,
            isIndeterminate,
            loadMoreRef
          )}
          onChange={handleChange}
          onClose={handleClose}
          style={{ width: '100%', maxHeight: 'max-content' }}
          sx={{
            '.MuiAutocomplete-inputRoot': {
              maxHeight: 'max-content !important',
            },
            ...(sx ?? null),
          }}
          ListboxComponent={CustomScrollbarListbox}
          renderInput={(params) => (
            <TextField
              {...params}
              label={InputProps?.label}
              placeholder={InputProps?.placeholder}
              helperText={InputProps?.helperText}
              InputProps={{
                ...params.InputProps,
                onChange: handleInputValue,
              }}
              sx={{
                '&.MuiInputBase-sizeSmall': {
                  minHeight: 42,
                },
              }}
              inputProps={{
                ...params.inputProps,
                'data-testid': dataTestId ?? 'autocomplete-' + id,
              }}
            />
          )}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (allSelectable) {
              return [
                { label: 'Select all', value: 'select-all' },
                ...filtered,
              ];
            }

            return filtered;
          }}
          inputValue={inputValue}
          open={open}
          slotProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-option': {
                  paddingX: 1,
                  paddingY: 0.75,
                  transition: 'background-color 0.2s',
                  cursor: 'pointer',
                },
                '& .MuiAutocomplete-option:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              },
            },
          }}
        />
      </Tooltip>
    );
  }
);

const Autocomplete: FC<
  Pick<
    ControlAutocompleteProps,
    | 'id'
    | 'options'
    | 'label'
    | 'allSelectable'
    | 'name'
    | 'required'
    | 'InputProps'
    | 'isLoading'
    | 'tooltipTitle'
    | 'renderTags'
    | 'sx'
    | 'innerlabel'
    | 'open'
  > &
    Partial<
      Pick<
        ControlAutocompleteProps,
        'onChange' | 'onClose' | 'loadMoreRef' | 'loading' | 'dataTestId'
      >
    >
> = ({
  id,
  options,
  label,
  allSelectable,
  name,
  required,
  InputProps,
  tooltipTitle,
  isLoading,
  renderTags,
  sx,
  innerlabel,
  onChange,
  onClose,
  open,
  loadMoreRef,
  loading,
  dataTestId,
}) => {
  const { control } = useFormContext();
  const theme = useTheme();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error: fieldError } }) => {
        const { onChange: fieldChange } = field;
        return (
          <FormControl sx={{ width: '100%' }}>
            {label && !innerlabel && (
              <Typography
                variant="subtitle2"
                sx={(t) => ({
                  color: t.palette.text.primary,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                })}
              >
                {label}
                {required && '*'}
                {isLoading && (
                  <CircularProgress sx={{ ml: 1 }} size={16} color="inherit" />
                )}
              </Typography>
            )}
            <ControlAutocomplete
              allSelectable={allSelectable}
              id={id}
              options={options}
              InputProps={InputProps}
              isLoading={isLoading}
              tooltipTitle={tooltipTitle}
              renderTags={renderTags}
              loadMoreRef={loadMoreRef}
              loading={loading}
              sx={{
                ...sx,
                '& .MuiOutlinedInput-root fieldset': {
                  borderColor: fieldError
                    ? theme.palette.error.main
                    : 'inherit',
                },
              }}
              innerlabel={innerlabel}
              {...field}
              onChange={(value: OptionValue[]) => {
                fieldChange(value);
                onChange ? onChange(value) : null;
              }}
              onClose={onClose}
              open={open}
              dataTestId={dataTestId}
            />
            {fieldError && (
              <Typography
                sx={{ mt: 0.5, pl: 0.5 }}
                variant="caption"
                color="error"
              >
                {fieldError.message}
              </Typography>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default Autocomplete;
